import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  Calendar,
} from 'lucide-react';
import ProjectProgress from '../dashboard/ProjectProgress';
import MilestoneList from '../../components/MilestoneList';
import CreateMilestone from '../../components/CreateMilestone';

interface Project {
  _id: string;
  title: string;
  description: string;
  status: string;
  progress: number;
  deadline: string;
  budget: {
    amount: number;
    currency: string;
  };
  team: any[];
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  category: string;
  priority: string;
  technologies: string[];
  createdAt: string;
  milestones: any[];
  attachments: any[];
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

// Loading skeleton for project details
const ProjectDetailsSkeleton: React.FC<{ theme: string }> = ({ theme }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div
          className={`w-10 h-10 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div>
          <div
            className={`h-8 w-64 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded animate-pulse mb-2`}
          ></div>
          <div
            className={`h-4 w-32 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded animate-pulse`}
          ></div>
        </div>
      </div>
    </div>

    <div
      className={`${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      } border rounded-lg shadow-sm`}
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <div
            className={`h-6 w-20 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded-full animate-pulse`}
          ></div>
          <div
            className={`h-6 w-24 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded-full animate-pulse`}
          ></div>
        </div>
        <div
          className={`h-4 w-full ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
          } rounded animate-pulse`}
        ></div>
        <div
          className={`h-4 w-3/4 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
          } rounded animate-pulse`}
        ></div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="space-y-2">
              <div
                className={`h-4 w-20 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded animate-pulse`}
              ></div>
              <div
                className={`h-6 w-32 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded animate-pulse`}
              ></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');

  const API_BASE_URL = getApiBaseUrl();

  const fetchProject = useCallback(
    async (showRefreshing: boolean = false) => {
      if (!id) {
        setError('Invalid project ID - no ID provided');
        setLoading(false);
        return;
      }

      // Validate ObjectId format (24 character hex string)
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!objectIdRegex.test(id)) {
        setError(`Invalid project ID format: ${id}`);
        setDebugInfo(
          `Project ID should be a 24-character hexadecimal string. Received: "${id}" (length: ${id.length})`
        );
        setLoading(false);
        return;
      }

      try {
        if (!project) {
          setLoading(true);
        }
        if (showRefreshing) {
          setRefreshing(true);
        }
        setError('');
        setDebugInfo('');

        const token = localStorage.getItem('accessToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const url = `${API_BASE_URL}/projects/${id}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (!data.project) {
            setError('Project not found in response');
            setDebugInfo(
              `API returned success but no project data. Response: ${JSON.stringify(
                data
              )}`
            );
          } else {
            setProject(data.project);
          }
        } else {
          const responseText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(responseText);
          } catch {
            errorData = { message: responseText };
          }

          if (response.status === 401) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            navigate('/login');
            return;
          } else if (response.status === 403) {
            setError('You are not authorized to view this project');
            setDebugInfo(
              `Access denied. User role: ${user?.role}, User ID: ${user?._id}`
            );
          } else if (response.status === 404) {
            setError('Project not found');
            setDebugInfo(
              `Project with ID "${id}" does not exist or you don't have access to it`
            );
          } else if (response.status === 400) {
            setError('Invalid request');
            setDebugInfo(`Bad request: ${errorData.message || responseText}`);
          } else {
            throw new Error(
              errorData.message || `HTTP ${response.status}: ${responseText}`
            );
          }
        }
      } catch (error: any) {
        setError(error.message || 'Failed to load project details');
        setDebugInfo(
          `Network error: ${error.message}. Check console for more details.`
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id, API_BASE_URL, navigate, project, user]
  );

  // Initial load
  useEffect(() => {
    if (id && user) {
      fetchProject();
    } else {
    }
  }, [id, user]);

  // Manual refresh
  const handleRefresh = useCallback(() => {
    fetchProject(true);
  }, [fetchProject]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchProject();
  }, [fetchProject]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'on-hold':
        return 'text-gray-600 bg-gray-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount
    );

  const getTimeUntilDeadline = (deadline: string) => {
    const days = Math.ceil(
      (new Date(deadline).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    return `${days} days left`;
  };

  // Loading state
  if (loading) {
    return <ProjectDetailsSkeleton theme={theme} />;
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard/projects"
              className={`p-2 ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
              } rounded-lg transition-colors duration-200`}
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1
                className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                Project Details
              </h1>
              <p
                className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } mt-1`}
              >
                Error loading project
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`p-2 ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            } rounded-lg transition-colors duration-200 disabled:opacity-50`}
            title="Refresh project"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
          </button>
        </div>

        <div
          className={`${
            theme === 'dark'
              ? 'bg-red-900/20 border-red-800'
              : 'bg-red-50 border-red-200'
          } border rounded-lg p-8 text-center`}
        >
          <AlertCircle
            className={`w-12 h-12 ${
              theme === 'dark' ? 'text-red-400' : 'text-red-500'
            } mx-auto mb-4`}
          />
          <h3
            className={`text-lg font-medium ${
              theme === 'dark' ? 'text-red-300' : 'text-red-800'
            } mb-2`}
          >
            Failed to load project
          </h3>
          <p
            className={`${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            } mb-4`}
          >
            {error}
          </p>

          {debugInfo && (
            <div
              className={`${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-gray-100 border-gray-300'
              } border rounded-lg p-4 mb-6 text-left`}
            >
              <h4
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } mb-2`}
              >
                Debug Information:
              </h4>
              <p
                className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } font-mono whitespace-pre-wrap`}
              >
                {debugInfo}
              </p>
              <div
                className={`mt-2 text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                <p>Project ID: {id}</p>
                <p>API Base URL: {API_BASE_URL}</p>
                <p>User Role: {user?.role}</p>
                <p>User ID: {user?._id}</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleRetry}
              className={`px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-red-600 hover:bg-red-700'
              } text-white rounded-lg transition-colors duration-200`}
            >
              Try Again
            </button>
            <Link
              to="/dashboard/projects"
              className={`px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              } rounded-lg transition-colors duration-200`}
            >
              Back to Projects
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No project found
  if (!project) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard/projects"
              className={`p-2 ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
              } rounded-lg transition-colors duration-200`}
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1
                className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                Project Not Found
              </h1>
            </div>
          </div>
        </div>

        <div
          className={`text-center py-12 ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } rounded-lg shadow-sm border`}
        >
          <AlertCircle
            className={`w-16 h-16 ${
              theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
            } mx-auto mb-4`}
          />
          <h3
            className={`text-lg font-medium ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-2`}
          >
            Project not found
          </h3>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mb-6`}
          >
            The project you're looking for doesn't exist or you don't have
            permission to view it.
          </p>
          <Link
            to="/dashboard/projects"
            className={`inline-flex items-center px-4 py-2 ${
              theme === 'dark'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/projects"
            className={`p-2 ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-300'
                : 'text-gray-600 hover:text-gray-900'
            } rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1
              className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              {project.title}
            </h1>
            <p
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mt-1`}
            >
              Project Details
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className={`p-2 ${
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          } rounded-lg transition-colors duration-200 disabled:opacity-50`}
          title="Refresh project"
        >
          <RefreshCw
            className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      <div
        className={`${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } border rounded-lg shadow-sm`}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center space-x-3">
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${getStatusColor(
                project.status
              )}`}
            >
              {project.status}
            </span>
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${getPriorityColor(
                project.priority
              )}`}
            >
              {project.priority} priority
            </span>
            <Link
              to={`/dashboard/projects/${project._id}/edit`}
              className="px-4 py-1 bg-blue-500 text-semibold text-white rounded-full"
            >
              Edit Project
            </Link>
          </div>

          <p
            className={`${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            {project.description}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                Progress
              </h3>
              <div className="flex items-center space-x-3">
                <div
                  className={`flex-1 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  } rounded-full h-3`}
                >
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
                <span
                  className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {project.progress}%
                </span>
              </div>
              <Link
                to={`/dashboard/projects/${project._id}/progress`}
                className="inline-block mt-2 text-sm text-blue-600 hover:underline"
              >
                View Full Progress & Charts
              </Link>
              <ProjectProgress projectId={project._id} />
            </div>

            <div>
              <h3
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                Deadline
              </h3>
              <div className="flex items-center space-x-2">
                <Calendar
                  className={`w-4 h-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <p
                  className={`${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {getTimeUntilDeadline(project.deadline)}
                </p>
              </div>
            </div>

            <div>
              <h3
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                Budget
              </h3>
              <div className="flex items-center space-x-2">
                <DollarSign
                  className={`w-4 h-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <p
                  className={`${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {formatCurrency(
                    project.budget.amount,
                    project.budget.currency
                  )}
                </p>
              </div>
            </div>

            <div>
              <h3
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                Client
              </h3>
              <div className="flex items-center space-x-2">
                <Users
                  className={`w-4 h-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <p
                  className={`${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {project.client.firstName} {project.client.lastName} (
                  {project.client.email})
                </p>
              </div>
            </div>
          </div>

          {project.technologies && project.technologies.length > 0 && (
            <div>
              <h3
                className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}
              >
                Technologies
              </h3>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, index) => (
                  <span
                    key={index}
                    className={`px-2 py-1 text-xs font-medium ${
                      theme === 'dark'
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-700'
                    } rounded-full`}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          <MilestoneList projectId={project._id} />

          {user?.role === 'admin' && (
            <CreateMilestone
              projectId={project._id}
              onMilestoneCreated={() => fetchProject()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
